import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Box,
  Container,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Divider,
  Chip,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Dialog,
  DialogContent,
  Stack,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  CheckCircle,
  EmojiEvents,
  CreditCard,
  TrendingUp,
  Info,
  Warning,
  ArrowForward,
} from '@mui/icons-material';
import { paymentAPI } from '../api/payment.api';
import { useAuth } from '../context/AuthContext';

const PageWrapper = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  backgroundColor: '#f8f9fa',
  padding: '2rem 0',
  [theme.breakpoints.down('md')]: {
    padding: '1rem 0',
  },
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.spacing(2),
  boxShadow: '0 2px 8px rgba(41, 30, 108, 0.1)',
  marginBottom: theme.spacing(3),
  border: '1px solid #e0e0e0',
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(3),
  padding: theme.spacing(2),
  borderRadius: theme.spacing(1),
  backgroundColor: 'rgba(41, 30, 108, 0.05)',
  borderLeft: `4px solid #291e6c`,
}));

const EventCard = styled(Card)(({ theme, highlight }) => ({
  border: highlight ? '3px solid #4caf50' : '2px solid #e0e0e0',
  borderRadius: theme.spacing(2),
  backgroundColor: highlight ? 'rgba(76, 175, 80, 0.05)' : 'white',
  position: 'relative',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 6px 16px rgba(41, 30, 108, 0.15)',
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  padding: '12px 32px',
  borderRadius: '8px',
  fontSize: '1rem',
  fontWeight: 600,
  textTransform: 'none',
  fontFamily: "'Oswald', sans-serif",
  backgroundColor: '#291e6c',
  '&:hover': {
    backgroundColor: '#1f1750',
  },
}));

const ProcessingDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: '16px',
    padding: theme.spacing(4),
    textAlign: 'center',
    minWidth: '400px',
    backgroundColor: '#fff',
    boxShadow: '0 8px 32px rgba(41, 30, 108, 0.3)',
    [theme.breakpoints.down('md')]: {
      minWidth: '90vw',
      padding: theme.spacing(3),
    },
  },
}));

const UpgradePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [upgradeData, setUpgradeData] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [eligible, setEligible] = useState(false);
  const [razorpayLoaded, setRazorpayLoaded] = useState(false);

  // Load Razorpay script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.onload = () => {
      console.log('Razorpay script loaded successfully');
      setRazorpayLoaded(true);
    };
    script.onerror = () => {
      console.error('Failed to load Razorpay script');
      toast.error('Failed to load payment gateway');
    };
    document.body.appendChild(script);

    return () => {
      // Cleanup: remove script when component unmounts
      const existingScript = document.querySelector('script[src="https://checkout.razorpay.com/v1/checkout.js"]');
      if (existingScript) {
        document.body.removeChild(existingScript);
      }
    };
  }, []);

  useEffect(() => {
    checkEligibility();
  }, []);

  const checkEligibility = async () => {
    try {
      const athleteId = localStorage.getItem('athleteId');
      if (!athleteId) {
        toast.error('Please login to continue');
        navigate('/login');
        return;
      }

      const response = await paymentAPI.checkUpgradeEligibility(athleteId);
      
      if (response.success && response.eligible) {
        setUpgradeData(response.data);
        setEligible(true);
      } else {
        setEligible(false);
        toast.info(response.message || 'You are not eligible for upgrade at this time');
        setTimeout(() => navigate('/dashboard'), 2000);
      }
    } catch (error) {
      console.error('Error checking eligibility:', error);
      toast.error(error.response?.data?.message || 'Failed to check eligibility');
      setTimeout(() => navigate('/dashboard'), 2000);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgradePayment = async () => {
    // Check if Razorpay is loaded
    if (!razorpayLoaded || !window.Razorpay) {
      toast.error('Payment gateway not loaded. Please refresh the page and try again.');
      return;
    }

    setProcessing(true);

    try {
      const athleteId = localStorage.getItem('athleteId');
      const newEventId = upgradeData.upgradeToEvent.eventId;

      // Create upgrade order
      const orderResponse = await paymentAPI.createUpgradeOrder(athleteId, newEventId);

      if (!orderResponse.success) {
        throw new Error(orderResponse.message || 'Failed to create upgrade order');
      }

      const { orderId, amount, key, athleteName, athleteEmail, athletePhone } = orderResponse.data;

      // Razorpay options
      const options = {
        key: key,
        amount: amount,
        currency: 'INR',
        name: 'Indian Weightlifting Federation',
        description: `Event Upgrade: ${upgradeData.currentEvent.eventName} → ${upgradeData.upgradeToEvent.eventName}`,
        order_id: orderId,
        prefill: {
          name: athleteName,
          email: athleteEmail,
          contact: athletePhone,
        },
        theme: {
          color: '#291e6c',
        },
        handler: async function (response) {
          try {
            const verifyResponse = await paymentAPI.verifyUpgradePayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              athleteId: athleteId,
            });

            if (verifyResponse.success) {
              localStorage.setItem('upgradeCompleted', 'true');
              localStorage.setItem('upgradeData', JSON.stringify(verifyResponse.data));
              toast.success('Upgrade payment successful!');
              navigate('/upgrade-success');
            } else {
              throw new Error(verifyResponse.message || 'Payment verification failed');
            }
          } catch (error) {
            console.error('Payment verification error:', error);
            toast.error(error.response?.data?.message || 'Payment verification failed');
            setProcessing(false);
          }
        },
        modal: {
          ondismiss: function () {
            setProcessing(false);
            toast.info('Payment cancelled');
          },
        },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.response?.data?.message || 'Failed to process payment');
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <PageWrapper>
        <Container maxWidth="md">
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
            <CircularProgress size={60} sx={{ color: '#291e6c' }} />
          </Box>
        </Container>
      </PageWrapper>
    );
  }

  if (!eligible || !upgradeData) {
    return (
      <PageWrapper>
        <Container maxWidth="md">
          <StyledPaper>
            <Alert severity="info" icon={<Info />}>
              <Typography variant="h6">Not Eligible for Upgrade</Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                You are not eligible for event upgrade at this time. Please check back later or contact support.
              </Typography>
            </Alert>
            <Box mt={3} display="flex" justifyContent="center">
              <Button
                variant="contained"
                onClick={() => navigate('/dashboard')}
                sx={{ backgroundColor: '#291e6c' }}
              >
                Back to Dashboard
              </Button>
            </Box>
          </StyledPaper>
        </Container>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <Container maxWidth="lg">
        {/* Header */}
        <Box textAlign="center" mb={4}>
          <Typography
            variant="h3"
            sx={{
              fontFamily: "'Oswald', sans-serif",
              fontWeight: 700,
              color: '#291e6c',
              mb: 1,
            }}
          >
            Upgrade Your Event Registration
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Upgrade from State to National level registration
          </Typography>
        </Box>

        {/* Info Alert */}
        <Alert severity="info" icon={<TrendingUp />} sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Great news!</strong> You can upgrade your registration to National (NIC) by paying only the differential amount.
            Your existing State (SIC) registration will be upgraded, and a new admit card will be generated.
          </Typography>
        </Alert>

        <Grid container spacing={3}>
          {/* Current Event */}
          <Grid item xs={12} md={6}>
            <EventCard>
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <EmojiEvents sx={{ color: '#ff9800', mr: 1, fontSize: 30 }} />
                  <Typography variant="h6" fontWeight="bold">
                    Current Registration
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                
                <Typography variant="h5" color="primary" fontWeight="bold" mb={1}>
                  {upgradeData.currentEvent.eventName}
                </Typography>
                
                <Box mt={2}>
                  <Stack direction="row" justifyContent="space-between" mb={1}>
                    <Typography variant="body2" color="text.secondary">
                      Amount Paid:
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      ₹{upgradeData.currentEvent.eventAmount}
                    </Typography>
                  </Stack>
                </Box>

                <Chip
                  label="Active"
                  color="success"
                  size="small"
                  sx={{ mt: 2 }}
                />
              </CardContent>
            </EventCard>
          </Grid>

          {/* Upgrade Event */}
          <Grid item xs={12} md={6}>
            <EventCard highlight>
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <EmojiEvents sx={{ color: '#4caf50', mr: 1, fontSize: 30 }} />
                  <Typography variant="h6" fontWeight="bold">
                    Upgrade To
                  </Typography>
                  <Chip
                    label="RECOMMENDED"
                    color="success"
                    size="small"
                    sx={{ ml: 'auto', fontWeight: 'bold' }}
                  />
                </Box>
                <Divider sx={{ mb: 2 }} />
                
                <Typography variant="h5" color="success.main" fontWeight="bold" mb={1}>
                  {upgradeData.upgradeToEvent.eventName}
                </Typography>
                
                <Box mt={2}>
                  <Stack direction="row" justifyContent="space-between" mb={1}>
                    <Typography variant="body2" color="text.secondary">
                      Total Amount:
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      ₹{upgradeData.upgradeToEvent.eventAmount}
                    </Typography>
                  </Stack>
                  
                  <Alert severity="success" sx={{ mt: 2 }}>
                    <Typography variant="body2" fontWeight="bold">
                      Pay only ₹{upgradeData.differentialAmount} to upgrade!
                    </Typography>
                  </Alert>
                </Box>
              </CardContent>
            </EventCard>
          </Grid>
        </Grid>

        {/* Price Breakdown */}
        <StyledPaper sx={{ mt: 3 }}>
          <SectionHeader>
            <CreditCard sx={{ fontSize: 28, color: '#291e6c' }} />
            <Typography variant="h5" fontWeight="bold">
              Payment Breakdown
            </Typography>
          </SectionHeader>

          <TableContainer>
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell>
                    <Typography variant="body1" fontWeight="500">
                      Differential Amount (Upgrade Fee)
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body1">
                      ₹{upgradeData.priceBreakdown.baseAmountInRupees}
                    </Typography>
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell>
                    <Typography variant="body1" fontWeight="500">
                      GST @ {upgradeData.priceBreakdown.gstRate}%
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body1">
                      ₹{upgradeData.priceBreakdown.gstAmountInRupees}
                    </Typography>
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell>
                    <Typography variant="body1" fontWeight="500">
                      Payment Gateway Charges @ {upgradeData.priceBreakdown.convenienceRate}%
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body1">
                      ₹{upgradeData.priceBreakdown.convenienceAmountInRupees}
                    </Typography>
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell colSpan={2}>
                    <Divider sx={{ my: 1 }} />
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell>
                    <Typography variant="h6" fontWeight="bold" color="primary">
                      Total Amount to Pay
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="h6" fontWeight="bold" color="primary">
                      ₹{upgradeData.priceBreakdown.totalAmountInRupees}
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          {/* Important Notes */}
          <Alert severity="warning" icon={<Warning />} sx={{ mt: 3 }}>
            <Typography variant="body2" fontWeight="bold" mb={1}>
              Important Information:
            </Typography>
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              <li>
                <Typography variant="body2">
                  Both invoices (original and upgrade) will be maintained for your records
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  A new admit card will be generated with updated event details
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  Your old admit card will be replaced but remains accessible in history
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  The upgrade is non-refundable and non-transferable
                </Typography>
              </li>
            </ul>
          </Alert>

          {/* Action Buttons */}
          <Box mt={4} display="flex" gap={2} justifyContent="center">
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/dashboard')}
              sx={{
                borderColor: '#291e6c',
                color: '#291e6c',
                '&:hover': {
                  borderColor: '#1f1750',
                  backgroundColor: 'rgba(41, 30, 108, 0.05)',
                },
              }}
            >
              Cancel
            </Button>

            <StyledButton
              variant="contained"
              size="large"
              onClick={handleUpgradePayment}
              disabled={processing || !razorpayLoaded}
              endIcon={processing ? <CircularProgress size={20} color="inherit" /> : <ArrowForward />}
            >
              {!razorpayLoaded 
                ? 'Loading Payment Gateway...' 
                : processing 
                  ? 'Processing...' 
                  : 'Proceed to Payment'}
            </StyledButton>
          </Box>
        </StyledPaper>

        {/* Processing Dialog */}
        <ProcessingDialog open={processing} disableEscapeKeyDown>
          <DialogContent>
            <CircularProgress size={60} sx={{ color: '#291e6c', mb: 2 }} />
            <Typography variant="h6" fontWeight="bold" mb={1}>
              Processing Payment
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please complete the payment in the Razorpay window...
            </Typography>
          </DialogContent>
        </ProcessingDialog>
      </Container>
    </PageWrapper>
  );
};

export default UpgradePage;
